import api from 'services/api';
import {
  selectHostList, selectHostsSelected, selectHostCount, selectLastSelected, allHostsAreSelected,
  selectCurrentHost,
  selectContextMenuXPos,
  selectContextMenuYPos,
  selectModalBulkUpdateField,
  selectModalBulkUpdateValue,
  selectSelectAllHosts,
  fetchGlobalHostsIsAllowed
} from 'store/Host/selectors';
import {
  selectQueryParam,
  selectExpandedQueryParam,
  selectIsGrouping,
  selectRowsPerPage,
  selectAdvancedFilterQueryParam,
  selectAdvancedExpandedQueryParam
} from 'store/Filters/selectors';
import {
  expandGroupBy,
  setGroupBy,
  clearGroupBy,
  setOrderBy,
  setPageNumber,
  setFilterError
} from 'store/Filters/actions';
import { trackEvent } from 'tracking/GA';
import { ACTIONS, CATEGORIES } from 'tracking/GA/constants';
import get from 'lodash/get';
import findIndex from 'lodash/findIndex';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import { selectCurrentWorkspace } from 'store/Faraday/selectors';
import * as types from './types';
import { getVulns, showAssetDetail } from 'store/Contextualization/AssetDetail/actions';
import { getHostsTags, getTags } from 'Screens/Contextualization/Tags/actions/Actions';
import { selectHostDetail, selectHostDetailId } from 'store/HostDetail/selectors';
import { selectAssetDetailId } from 'store/Contextualization/AssetDetail/selectors';
import { showHostDetail } from 'store/HostDetail/actions';
import { selectPathname } from 'store/Router/selectors';
import { refreshExploreGroupHosts } from 'store/GroupVulns/actions';
import { selectIsExploreGroupOpen } from 'store/GroupVulns/selectors';
import { closeModal, openModal } from 'store/modals/actions';
import { MODAL_MANAGE_BULK_UPDATE, MODAL_MANAGE_BULK_UPDATE_CONFIRMATION } from 'store/modals/modals';
import { ADD_COMMAND, showProcessingQueue } from 'Common/Components/FileUploadContext/Context/actions';

const setHostsFilterError = () => async (dispatch) => dispatch({ type: types.SET_HOSTS_FILTER_ERROR });

const getDataStartCallback = () => async (dispatch) => dispatch({ type: types.GET_DATA_HOST_START });
const getDataSuccedCallback = (data, hostCount) => async (dispatch) => dispatch({ type: types.GET_DATA_HOST_SUCCESS, data, hostCount });
const getDataFailureCallback = (error) => async (dispatch) => dispatch({ type: types.GET_DATA_HOST_FAIL, error });
const exploreGroupRoutePattern = /^\/vulns\/[^/]+\/groups\/[^/]+$/;
const hostDetailRoutePattern = /^\/host\/[^/]+\/([^/]+)$/;

const isExploreGroupContext = (state) =>
  selectIsExploreGroupOpen(state) || exploreGroupRoutePattern.test(selectPathname(state) || '');

const canKeepAssetDetailOpen = (state, hostId) => {
  const pathname = selectPathname(state) || '';
  if (pathname.startsWith('/manage/') || exploreGroupRoutePattern.test(pathname)) return true;

  const hostDetailRouteMatch = pathname.match(hostDetailRoutePattern);
  return !!hostDetailRouteMatch && String(hostDetailRouteMatch[1]) === String(hostId);
};

export function getTotalHosts () {
  return async (dispatch) => {
    const hosts = await api.host.getGlobalHosts(); // obtengo el total de assets.
    dispatch({ type: types.GET_TOTAL_HOSTS, totalHosts: hosts.total_hosts });
  };
}

export function getLimitReached () {
  return async (dispatch) => {
    const config = await api.faraday.fetchApiConfig();
    dispatch({ type: types.GET_HOSTS_LIMIT_REACHED, limitReached: get(config, 'total_assets_limit_reached', false) });
  };
}
// Summary: Get data to populate on table, with pagination and/or sort.
export function getData (ws) {
  return async (dispatch, getState) => {
    dispatch(getDataStartCallback());

    const state = getState();

    let advancedFilterQueryParam = [];
    try {
      advancedFilterQueryParam = selectAdvancedFilterQueryParam(state, 'assets');
    } catch (e) {
      dispatch(setFilterError('assets', 'Syntax error. Please try again. For further help check our documentation'));
      return dispatch(setHostsFilterError());
    }
    const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
    const standardQueryParam = selectQueryParam('assets', state);
    const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

    try {
      const workspace = selectCurrentWorkspace(getState());
      const response = await api.host.fetchHosts(ws || workspace, queryParam);
      const data = response.rows.map((x) => x.value);

      // Both belong to the same license-limit feature (absent from community's own
      // /config response); gate them together instead of leaving getLimitReached's
      // GET /config as a leftover, always-firing call on an edition with no such limit.
      if (fetchGlobalHostsIsAllowed(getState())) {
        dispatch(getTotalHosts());
        dispatch(getLimitReached());
      }
      return dispatch(getDataSuccedCallback(data, response.count));
    } catch (e) {
      const errorMessage = get(e, 'message', 'Failed to obtain hosts.');
      if (errorMessage.includes('filter')) {
        dispatch(setFilterError('assets', 'Invalid filters. Please try again. For further help check our documentation'));
        return dispatch(setHostsFilterError());
      }
      return dispatch(getDataFailureCallback(errorMessage));
    }
  };
}

export function updateHostList (hostList) {
  return (dispatch) => {
    dispatch({ type: types.UPDATE_DATA_HOST_LIST, hostList });
  };
}

export function appendHostListRow (hostRow) {
  return (dispatch) => {
    dispatch({ type: types.APPEND_DATA_HOST_LIST, hostRow });
  };
}

export function updateHostListRow (hostRow) {
  return async (dispatch, getState) => {
    const state = getState();
    const hostList = selectHostList(state);
    const newHostList = [...hostList];
    const hostIndex = newHostList.findIndex((h) => h.id === hostRow.id);
    if (hostIndex >= 0) {
      newHostList[hostIndex] = hostRow;
      dispatch(updateHostList(newHostList));
    }
  };
}

export function setOrderByAssets (sorting) {
  return (dispatch, getState) => {
    const state = getState();
    const isGrouping = selectIsGrouping('assets', state);

    dispatch(setOrderBy('assets', sorting));
    if (!isGrouping) dispatch(getData());
  };
}

export function setPageNumberAssets (pageNumber) {
  return (dispatch) => {
    dispatch(setPageNumber('assets', pageNumber));
    dispatch(getData());
  };
}

// ------------- GROUP BY ACTIONS -------------

export function getExpandedGroupByData (index) {
  return async (dispatch, getState) => {
    const state = getState();
    dispatch(getDataStartCallback());

    let advancedFilterQueryParam = [];
    try {
      advancedFilterQueryParam = selectAdvancedExpandedQueryParam(state, 'assets');
    } catch (e) {
      dispatch(setFilterError('assets', 'Syntax error. Please try again. For further help check our documentation'));
      return dispatch(setHostsFilterError());
    }
    const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
    const standardQueryParam = selectExpandedQueryParam('assets', state);
    const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

    try {
      const workspace = selectCurrentWorkspace(getState());
      const response = await api.host.fetchHosts(workspace, queryParam);
      const data = response && response.rows ? response.rows.map((data) => data.value) : [];
      return dispatch({ type: types.GET_DATA_HOST_GROUP_BY_EXPAND, index, data });
    } catch (e) {
      const errorMessage = get(e, 'message', 'Failed to obtain hosts.');
      if (errorMessage.includes('filter')) {
        dispatch(setFilterError('assets', 'Invalid filters. Please try again. For further help check our documentation'));
        return dispatch(setHostsFilterError());
      }
      return dispatch(getDataFailureCallback(errorMessage));
    }
  };
}

export function clearExpandedGroupByData () {
  return async (dispatch) => dispatch({ type: types.GET_DATA_HOST_GROUP_BY_RESET });
}

export function clearGroupByAssets () {
  return async (dispatch) => {
    await dispatch(clearGroupBy('assets'));
  };
}

export function setGroupByAssets (group_by) {
  return async (dispatch) => {
    const field = get(group_by, '[0].field', '');
    await dispatch(setGroupBy('assets', group_by));
    if (field) await dispatch(setOrderBy('assets', [{ id: field, desc: false }]));
    dispatch(getData());
  };
}

export function expandGroupByAssets (index, data) {
  return async (dispatch) => {
    await dispatch(expandGroupBy('assets', data));
    dispatch(getExpandedGroupByData(index));
  };
}

// -------------  -------------

export function toggleModalEditCreate (value, host) {
  return (dispatch) => {
    dispatch({ type: types.TOGGLE_MODAL_EDIT_CREATE_HOST, value, host });
  };
}

export function resetState () {
  return (dispatch) => {
    dispatch({ type: types.RESET_STATE_HOST });
  };
}

// Summary: Show confirmation modal when user delete host/s
export function showHostModalDelete () {
  return (dispatch) => {
    dispatch({ type: types.SHOW_MODAL_DELETE_HOSTS });
  };
}

// Summary: Hide confirmation modal when user delete host/s
export function hideHostModalDelete () {
  return (dispatch) => {
    dispatch({ type: types.HIDE_MODAL_DELETE_HOSTS });
  };
}

export function deleteHostsSelected () {
  return async (dispatch, getState) => {
    dispatch({ type: types.DELETE_HOSTS_START });
    const { workspaceSelected } = getState().faraday;
    const hostselected = selectHostsSelected(getState());
    const hostSelectedID = hostselected.map(host => {
      dispatch(trackEvent(CATEGORIES.asset, ACTIONS.deleteAseet.name, ACTIONS.deleteAseet.label, host._id));
      return host._id;
    });
    try {
      const response = await api.host.deleteHost(workspaceSelected, hostSelectedID);
      dispatch({ type: types.DELETE_HOSTS_SUCCESS, response });

      dispatch(hideHostModalDelete());
      dispatch(getData());
    } catch (err) {
      dispatch({ type: types.DELETE_HOSTS_FAIL });
    }
  };
}

export function showHostLeftFilters (visible) {
  return (dispatch) => {
    dispatch({ type: types.SHOW_HOST_LEFT_FILTERS, visible });
  };
}

// Click Logic

const addDeleteController = (hosts, hostList) => { // if all hosts are in hostList then unselect then if not
  const allHostsAreSelected = hosts.every((testHost) => hostList.some((host) => host._id === testHost._id));

  if (allHostsAreSelected) return [types.HOST_UNSELECTED, hosts];
  return [types.HOST_SELECTED, hosts];
};

const selectCalculator = (e, host, hostList, areHostSelected) => (dispatch, getState) => {
  const pivot = selectLastSelected(getState());
  const index = hostList.findIndex((el) => el._id === host._id);
  const hostsSelected = selectHostsSelected(getState());

  dispatch({ type: types.NEW_PIVOT, payload: index });
  if (e.shiftKey && pivot !== -1 && areHostSelected) { // if already had hosts selected and shift key is pressed
    const start = Math.min(pivot, index);
    const end = Math.max(pivot, index) + 1;
    const hosts = hostList.slice(start, end);
    const [type, payload] = addDeleteController(hosts, hostsSelected);
    return dispatch({ type, payload });
  }
  const [type, payload] = addDeleteController([host], hostsSelected);
  return dispatch({ type, payload });
};

export const selectRow = (e, host) => (dispatch, getState) => {
  const allHost = selectHostList(getState());
  const isGroupingBy = selectIsGrouping('assets', getState());
  const hostSelected = selectHostsSelected(getState());

  if (isGroupingBy) {
    const group = allHost.find((g) => g.groupData && g.groupData.find((el) => el._id === host._id));
    if (!group) return; // si no hay groupData no se puede hacer calculos con las hosts
    const hostGroup = group.groupData;
    const areHostSelected = hostSelected.length > 0 && hostGroup.some((el) => el.id === host.id);
    dispatch(selectCalculator(e, host, hostGroup, areHostSelected, hostSelected));
  } else {
    dispatch(selectCalculator(e, host, allHost, hostSelected.length > 0));
  }
};

export const unSelectAll = () => (dispatch) => dispatch({ type: types.UNSELECT_ALL_HOST });

export const selectAllHost = () => (dispatch, getState) => {
  const hostsList = selectHostList(getState());
  const hostsSelected = selectHostsSelected(getState());
  const hostCount = selectHostCount(getState());
  const pageSize = selectRowsPerPage('assets', getState());

  if (hostsSelected.length === hostCount || hostsSelected.length >= pageSize) return dispatch(unSelectAll());

  return dispatch({ type: types.SELECT_ALL_HOST, hostsList });
};

export function updateHost (hostId, valueObj) {
  return async (dispatch, getState) => {
    const state = getState();
    const workspaceSelected = selectCurrentWorkspace(state);
    const hostList = selectHostList(state);
    const hostCount = selectHostCount(state);
    const hostDetailId = selectHostDetailId(state);
    const assetDetailId = selectAssetDetailId(state);
    const newHost = await api.host.updateHost(workspaceSelected, { id: hostId, ...valueObj });
    const index = hostList.findIndex((x) => String(hostId) === String(x.id) || String(hostId) === String(x._id));

    if (index > -1) {
      const updatedHostList = [...hostList];
      updatedHostList[index] = { ...newHost, severity_counts: hostList[index].severity_counts };
      dispatch({ type: types.GET_DATA_HOST_SUCCESS, data: updatedHostList, hostCount });
    }

    const nextState = getState();
    const newHostId = get(newHost, '_id', get(newHost, 'id', hostId));
    if (hostDetailId && String(newHostId) === String(hostDetailId)) dispatch(showHostDetail(newHost));
    if (assetDetailId && String(newHostId) === String(assetDetailId) && canKeepAssetDetailOpen(nextState, newHostId)) dispatch(showAssetDetail(newHost));
    if (isExploreGroupContext(nextState)) await dispatch(refreshExploreGroupHosts());
  };
}

export function setAssetsTags (tagsToAdd = [], tagsToRemove = []) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const hostsSelected = selectHostsSelected(state);
      const hostsList = selectHostList(state);
      const selectedHostsIds = hostsSelected.map((h) => (h._id));
      const workspace = state.faraday.workspaceSelected;
      const selectAll = allHostsAreSelected(state);

      const advancedFilterQueryParam = selectAdvancedFilterQueryParam(state, 'assets');
      const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
      const standardQueryParam = selectQueryParam('assets', state);
      const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

      if (selectAll) {
        const data = {
          tags_to_add: tagsToAdd,
          tags_to_remove: tagsToRemove
        };

        await api.host.setAllHostsTags(workspace, { filters: queryParam.filters }, data);
      } else {
        const data = {
          host_ids: selectedHostsIds,
          tags_to_add: tagsToAdd,
          tags_to_remove: tagsToRemove
        };

        await api.host.setHostsTags(workspace, data);
      }

      const newTags = tagsToAdd.filter((tag) => !tagsToRemove.includes(tag));

      selectedHostsIds.forEach((id) => {
        const host = find(hostsSelected, { _id: id });
        const newTagsList = newTags.filter((tag) => !host.tags.includes(tag));
        host.tags = [
          ...host.tags.filter((tag) => !tagsToRemove.includes(tag)),
          ...newTagsList
        ];

        const indexHostsList = findIndex(hostsList, { _id: host._id });
        if (indexHostsList > -1 && !isEmpty(hostsList)) hostsList.splice(indexHostsList, 1, host);

        const indexVulnsSelected = findIndex(hostsSelected, { _id: id });
        if (indexHostsList > -1 && !isEmpty(hostsSelected)) hostsSelected.splice(indexVulnsSelected, 1, host);
      });

      dispatch({ type: types.SET_TAGS_SUCCESS });

      dispatch(getTags());
      dispatch(getHostsTags());
    } catch (e) {
      dispatch({ type: types.SET_TAGS_FAIL, error: e.message });
    }
  };
}

export function addTag (tag) {
  return (dispatch) => {
    dispatch(setAssetsTags([tag], []));
  };
}

export function removeTag (tag) {
  return (dispatch) => {
    dispatch(setAssetsTags([], [tag]));
  };
}

// Context
export function updateAsset (hostId, valueObj) {
  return async (dispatch, getState) => {
    const state = getState();
    const workspaceSelected = selectCurrentWorkspace(state);
    const hostList = selectHostList(state);
    const hostCount = selectHostCount(state);
    const hostDetailId = selectAssetDetailId(state);
    const newHost = await api.host.updateHost(workspaceSelected, { id: hostId, ...valueObj });
    const index = hostList.findIndex((x) => String(hostId) === String(x.id) || String(hostId) === String(x._id));

    if (index > -1) {
      const updatedHostList = [...hostList];
      updatedHostList[index] = { ...newHost, severity_counts: hostList[index].severity_counts };
      dispatch({ type: types.GET_DATA_HOST_SUCCESS, data: updatedHostList, hostCount });
    }

    const nextState = getState();
    const newHostId = get(newHost, '_id', get(newHost, 'id', hostId));
    if (hostDetailId && String(newHostId) === String(hostDetailId) && canKeepAssetDetailOpen(nextState, newHostId)) dispatch(showAssetDetail(newHost));
    if (isExploreGroupContext(nextState)) await dispatch(refreshExploreGroupHosts());
  };
}

export function setOrderByVulnsInHost (sorting) {
  return async (dispatch, getState) => {
    const state = getState();
    const host = selectCurrentHost(state);
    const hostIp = get(host, 'ip', '');
    dispatch(setOrderBy('vulnsAssets', sorting));
    if (hostIp) await dispatch(getVulns(host.ip));
  };
}

export function removeCurrentHost () {
  return (dispatch) => {
    dispatch({ type: types.REMOVE_CURRENT_HOST });
  };
}

export function appendAssetListRow (hostRow) {
  return (dispatch) => {
    dispatch({ type: types.ASSETS_APPEND_DATA_HOST_LIST, hostRow });
  };
}

const addDeleteControllerAsset = (hosts, hostList) => { // if all hosts are in hostList then unselect then if not
  const allHostsAreSelected = hosts.every((testHost) => hostList.some((host) => host._id === testHost._id));

  if (allHostsAreSelected) return [types.ASSET_UNSELECTED, hosts];
  return [types.ASSET_SELECTED, hosts];
};

const selectCalculatorAsset = (e, host, hostList, areHostSelected) => (dispatch, getState) => {
  const pivot = selectLastSelected(getState());
  const index = hostList.findIndex((el) => el._id === host._id);
  const hostsSelected = selectHostsSelected(getState());

  dispatch({ type: types.NEW_PIVOT, payload: index });
  if (e.shiftKey && pivot !== -1 && areHostSelected) { // if already had hosts selected and shift key is pressed
    const start = Math.min(pivot, index);
    const end = Math.max(pivot, index) + 1;
    const hosts = hostList.slice(start, end);
    const [type, payload] = addDeleteControllerAsset(hosts, hostsSelected);
    return dispatch({ type, payload });
  }
  const [type, payload] = addDeleteControllerAsset([host], hostsSelected);
  return dispatch({ type, payload });
};

export const selectAssetRow = (e, host) => (dispatch, getState) => {
  const allHost = selectHostList(getState());
  const isGroupingBy = selectIsGrouping('assets', getState());
  const hostSelected = selectHostsSelected(getState());

  if (isGroupingBy) {
    const group = allHost.find((g) => g.groupData && g.groupData.find((el) => el._id === host._id));
    if (!group) return; // si no hay groupData no se puede hacer calculos con las hosts
    const hostGroup = group.groupData;
    const areHostSelected = hostSelected.length > 0 && hostGroup.some((el) => el.id === host.id);
    dispatch(selectCalculatorAsset(e, host, hostGroup, areHostSelected, hostSelected));
  } else {
    dispatch(selectCalculatorAsset(e, host, allHost, hostSelected.length > 0));
  }
};

export const selectTotalHosts = () => (dispatch, getState) => {
  return dispatch({ type: types.SELECT_TOTAL_ASSETS });
};

export const unselectTotalHosts = () => (dispatch, getState) => {
  return dispatch({ type: types.UNSELECT_TOTAL_ASSETS });
};

export const showContextMenu = (show, XPos, YPos) => {
  return (dispatch, getState) => {
    const state = getState();
    const currentXPos = selectContextMenuXPos(state);
    const currentYPos = selectContextMenuYPos(state);
    const newXPos = XPos ? (XPos + 1) : currentXPos;
    const newYPos = YPos ? (YPos + 1) : currentYPos;

    dispatch({
      type: types.ASSETS_SHOW_CONTEXT_MENU, show, contextMenuXPos: newXPos, contextMenuYPos: newYPos
    });
  };
};

export const setBulkUpdateField = (field) => (dispatch) => dispatch({ type: types.ASSETS_SET_BULK_UPDATE_FIELD, field });

export const setBulkUpdateValue = (value) => (dispatch) => dispatch({ type: types.ASSETS_SET_BULK_UPDATE_VALUE, value });

export const addBulkUpdateValue = (value) => (dispatch) => dispatch({ type: types.ASSETS_ADD_BULK_UPDATE_VALUE, value });

export const removeBulkUpdateValue = (value) => (dispatch) => dispatch({ type: types.ASSETS_REMOVE_BULK_UPDATE_VALUE, value });

export const refreshAssetsList = (assetsList, assetsSelected, assetDetail) => (dispatch, getState) => {
  dispatch({
    type: types.ASSETS_REFRESH_LIST, assetsList, assetsSelected, assetDetail, assetsCount: selectHostCount(getState())
  });
};

export function bulkUpdateAssets () {
  return async (dispatch, getState) => {
    const state = getState();

    dispatch({ type: types.ASSETS_CONFIRMATION_CHANGE_START });

    try {
      const assetsList = selectHostList(state);
      const assetsSelected = selectHostsSelected(state);
      const assetDetail = selectHostDetail(state);
      const field = selectModalBulkUpdateField(state);
      const value = selectModalBulkUpdateValue(state);
      const workspaceSelected = selectCurrentWorkspace(state);
      const selectAll = selectSelectAllHosts(state);
      const assetIDs = assetsSelected.map((v) => v._id);

      const advancedFilterQueryParam = selectAdvancedFilterQueryParam(state, 'assets');
      const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
      const standardQueryParam = selectQueryParam('assets', state);
      const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

      let data = {};

      if (field === 'hostnames') data = { hostnames: value };
      else data = { [field]: value };

      const isDetailAssetSelected = !isEmpty(assetDetail) && assetsSelected.some((selectedAsset) => selectedAsset._id === assetDetail?._id);

      const updatedAssetDetail = isDetailAssetSelected ? { ...assetDetail, ...data } : { ...assetDetail };

      if (selectAll) {
        await api.host.updateAllHosts(workspaceSelected, { filters: queryParam.filters }, data);

        const updatedAssetsList = assetsList.map((asset) => ({ ...asset, ...data }));
        const updatedSelectedAssets = assetsSelected.map((asset) => ({ ...asset, ...data }));

        dispatch(refreshAssetsList(updatedAssetsList, updatedSelectedAssets, updatedAssetDetail));
      } else {
        const payload = { ids: assetIDs, ...data };

        await api.host.updateHosts(workspaceSelected, payload);

        const updatedAssetsList = assetsList.map((asset) => {
          const isSelected = assetsSelected.some((selectedAsset) => selectedAsset._id === asset._id);
          if (isSelected) return { ...asset, ...data };
          return asset;
        });

        const updatedSelectedAssets = assetsSelected.map((asset) => ({ ...asset, ...data }));

        dispatch({ type: types.ASSETS_SET_BULK_UPDATE_SUCCESS });
        dispatch(refreshAssetsList(updatedAssetsList, updatedSelectedAssets, updatedAssetDetail));
      }

      dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE));
      dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE_CONFIRMATION));
      dispatch({ type: types.ASSETS_BULK_UPDATE_FINISHED });
    } catch (e) {
      dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE));
      dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE_CONFIRMATION));
      dispatch({ type: types.ASSETS_ERROR, errorMessage: e.message || 'An error occured while updating vulns' });
    }
  };
}

export const showBulkUpdateModal = (bulkUpdateField, bulkUpdateValue) => {
  return (dispatch) => {
    dispatch(setBulkUpdateField(bulkUpdateField));
    dispatch(setBulkUpdateValue(bulkUpdateValue));
    dispatch(openModal(MODAL_MANAGE_BULK_UPDATE));
  };
};

export function deleteAssetsSelected () {
  return async (dispatch, getState) => {
    dispatch({ type: types.DELETE_HOSTS_START });
    const { workspaceSelected } = getState().faraday;
    const hostselected = selectHostsSelected(getState());
    const hostSelectedID = hostselected.map(host => host._id);
    const selectAll = selectSelectAllHosts(getState());

    const advancedFilterQueryParam = selectAdvancedFilterQueryParam(getState(), 'assets');
    const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
    const standardQueryParam = selectQueryParam('assets', getState());
    const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

    try {
      if (selectAll) {
        await api.host.deleteAllHosts(workspaceSelected, { filters: queryParam.filters });
      } else {
        const response = await api.host.deleteHost(workspaceSelected, hostSelectedID);
        dispatch({ type: types.DELETE_HOSTS_SUCCESS, response });
      }

      dispatch(hideHostModalDelete());
      dispatch(getData());
      dispatch(unSelectAll());
    } catch (err) {
      dispatch({ type: types.DELETE_HOSTS_FAIL });
    }
  };
}

export const moveAssetsToWorkspace = (destinationWs, replace) => async (dispatch, getState) => {
  const currentWs = selectCurrentWorkspace(getState());
  const hostsSelected = selectHostsSelected(getState());
  const asset_ids = hostsSelected.map(host => host._id);
  const destination_workspace_id = get(destinationWs, '_id', 0);
  const destinationWsName = get(destinationWs, 'name', 0);
  const mode = replace ? 'replace' : 'ignore';
  try {
    const data = {
      asset_ids,
      destination_workspace_id,
      mode
    };

    const response = await api.host.moveAssets(currentWs, data);
    if (response?.command_id) {
      dispatch({
        type: ADD_COMMAND,
        command: {
          command_id: response.command_id,
          commandType: 'moveAssets',
          error: false,
          finished: false,
          assetsTotal: asset_ids?.length,
          destinationWsName
        }
      });
      dispatch(showProcessingQueue(true));
    }
    dispatch(unSelectAll());
    dispatch({ type: types.MOVE_ASSETS_TO_WORKSPACE, response });
  } catch (error) {
  }
};
